/**
 * PolicyInformationCollapse
 *
 * The collapsible "Policy Information" panel shared by the Insurance
 * Breakdown modal and the Insurance Estimates modal so the two stay
 * pixel-identical. Renders a summary row in the collapse header and, when
 * expanded, the full Patient / Member / Plan field grid with inline-editable
 * policy fields.
 *
 * State the parent owns and passes in:
 *   - selectedPolicy            base policy WITH any local overrides applied
 *   - patient                   the patient object (read-only patient fields)
 *   - policyCollapseActiveKey   controlled expand state
 *   - setPolicyCollapseActiveKey
 *   - carrierOptions            [{value,label}] for the Carrier select
 *   - isLoadingCarriers         spinner flag for that select
 *   - setPolicyOverrides        optimistic local merge after a field save
 *   - onPoliciesChanged         optional refetch callback after a save
 *
 * Extracted verbatim from InsuranceBreakdownMasterModal — behavior unchanged.
 */

import React, { useEffect, useState } from "react";
import { Button, Collapse, DatePicker, Input, Select, message } from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { controller as rootController } from "../../../../controller";
import MemberDobSuggestion from "./MemberDobSuggestion";
import { describeRepoint } from "../../lib/carrierLabel";
import { PLAN_DETAIL_GROUPS, PLAN_DETAIL_ROWS, planDetailText } from "../../lib/planDetails";

// The annual dollar maximum resets the day AFTER the benefit-year end date.
function formatAnnualMaxReset(endIso) {
  if (!endIso) return null;
  return dayjs(endIso).add(1, "day").format("MMM D, YYYY");
}

// Benefit year rendered as a start–end date range (falls back gracefully).
function formatBenefitYearRange(start, end) {
  if (!start && !end) return "—";
  if (start && end) return `${dayjs(start).format("MMM D, YYYY")} – ${dayjs(end).format("MMM D, YYYY")}`;
  return dayjs(start || end).format("MMM D, YYYY");
}

// "Aug 1, 2026 – Jul 31, 2027 · policy year"; just "Policy year" before the
// dates are known, so the header never reads "— · policy year".
function benefitYearSummary(policy) {
  // The period containing today when the server sends it (a stored year that
  // has ended is rolled forward); else the dates as stored.
  const bp = policy.benefit_period && policy.benefit_period.start ? policy.benefit_period : null;
  const range = bp ? formatBenefitYearRange(bp.start, bp.end)
    : formatBenefitYearRange(policy.benefit_year_start, policy.benefit_year_end);
  const type = policy.benefit_period_type && policy.benefit_period_type !== "unknown"
    ? planDetailText(PLAN_DETAIL_ROWS.find(function (r) { return r.field === "benefit_period_type"; }),
      policy.benefit_period_type)
    : "";
  if (!type) return range;
  return range === "—" ? type : range + " · " + type.toLowerCase();
}

const relationOptions = [
  { value: "self", label: "Self" },
  { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" },
  { value: "other", label: "Other" },
];
const ordinalOptions = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "tertiary", label: "Tertiary" },
  { value: "other", label: "Other" },
];

function formatDateValue(d) {
  if (d == null || d === "") return null;
  if (typeof d === "string") return d.split("T")[0];
  return String(d);
}

function formatPatientDobSummary(p) {
  if (!p) return "—";
  const birthDate = p.birth_date || p.date_of_birth;
  if (!birthDate) return "—";
  return formatDateValue(birthDate) || "—";
}

function formatPatientAddressSummary(p) {
  if (!p) return "—";
  const parts = [p.address, p.city, p.state, p.zip_code].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

function policySummaryRow(label, value) {
  const display = value != null && value !== "" ? value : "—";
  return (
    <div
      key={label}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 180px) 1fr",
        gap: "8px 16px",
        alignItems: "start",
        padding: "4px 0",
      }}
    >
      <div style={{ color: "#848696", fontSize: 13 }}>{label}</div>
      <div style={{ fontSize: 14, wordBreak: "break-word" }}>{display}</div>
    </div>
  );
}

function policySubsectionTitle(text) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#6b43b5",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 10,
        marginTop: 4,
      }}
    >
      {text}
    </div>
  );
}

function EditablePolicyField({ label, value, displayValue: displayValueProp, fieldName, policyId, onSaved, type, options, selectWidth, loading, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  const displayValue = (() => {
    if (displayValueProp != null && displayValueProp !== "") return String(displayValueProp);
    if (type === "boolean") return value === true ? "Yes" : value === false ? "No" : "—";
    if (type === "date") {
      if (!value) return "—";
      if (typeof value === "string") return value.split("T")[0];
      return String(value);
    }
    if ((type === "select" || type === "choice") && Array.isArray(options)) {
      const match = options.find(function (o) { return o.value === value; });
      if (match) return match.label;
    }
    if (value == null || value === "") return "—";
    return String(value);
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload = {};
      if (type === "integer") {
        payload[fieldName] = draft === "" || draft == null ? null : parseInt(draft, 10);
      } else if (type === "decimal") {
        payload[fieldName] = draft === "" || draft == null ? null : String(draft);
      } else if (type === "boolean") {
        payload[fieldName] = draft;
      } else if (type === "choice") {
        // A cleared choice is sent as null so it clears; a plain select's
        // undefined would drop the key and save nothing.
        payload[fieldName] = draft == null || draft === "" ? null : draft;
      } else if (type === "date") {
        if (draft == null || draft === "") {
          payload[fieldName] = null;
        } else if (dayjs.isDayjs(draft)) {
          payload[fieldName] = draft.format("YYYY-MM-DD");
        } else {
          payload[fieldName] = draft;
        }
      } else {
        payload[fieldName] = draft === "" ? null : draft;
      }
      const res = await rootController.updatePatientInsurancePolicy(policyId, payload);
      if (res.status === 200) {
        message.success(`${label} updated`);
        const repointNote = describeRepoint(res.json && res.json.mapping_repointed);
        if (repointNote) message.info(repointNote, 6);
        setEditing(false);
        if (onSaved) onSaved(res.json);
      } else {
        message.error((res.json && res.json.error) || "Failed to update");
      }
    } catch (err) {
      message.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const renderEditControl = () => {
    if (type === "boolean") {
      return (
        <Select
          size="small"
          value={draft == null ? undefined : draft}
          onChange={(v) => setDraft(v)}
          style={{ width: 80 }}
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
        />
      );
    }
    if (type === "select" || type === "choice") {
      return (
        <Select
          size="small"
          value={draft == null || draft === "" ? undefined : draft}
          onChange={(v) => setDraft(v)}
          style={{ width: selectWidth || 180 }}
          options={options || []}
          loading={loading}
          showSearch
          allowClear
          filterOption={function (input, option) {
            return String(option.label || "").toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
        />
      );
    }
    if (type === "date") {
      const dayjsVal = !draft ? null : (dayjs.isDayjs(draft) ? draft : dayjs(draft, "YYYY-MM-DD"));
      return (
        <DatePicker
          size="small"
          value={dayjsVal && dayjsVal.isValid() ? dayjsVal : null}
          onChange={(d) => setDraft(d)}
          style={{ width: 150 }}
          format="YYYY-MM-DD"
        />
      );
    }
    return (
      <Input
        size="small"
        value={draft != null ? draft : ""}
        onChange={(e) => setDraft(e.target.value)}
        // Numbers need only a small box; free text is often a full phrase, and
        // at 160px you cannot see what you typed.
        style={{ width: type === "integer" || type === "decimal" ? 160 : "100%", maxWidth: 280 }}
        type={type === "integer" || type === "decimal" ? "number" : "text"}
        step={type === "decimal" ? "0.01" : undefined}
        placeholder={placeholder}
        autoFocus
        onPressEnter={handleSave}
      />
    );
  };

  if (editing) {
    return (
      <div
        key={label}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(140px, 180px) 1fr",
          gap: "8px 16px",
          alignItems: "center",
          padding: "4px 0",
        }}
      >
        <div style={{ color: "#848696", fontSize: 13 }}>{label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {renderEditControl()}
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            loading={saving}
            onClick={handleSave}
            style={{ color: "#52c41a", padding: 0, minWidth: 0 }}
          />
          <Button
            type="link"
            size="small"
            icon={<CloseOutlined />}
            disabled={saving}
            onClick={handleCancel}
            style={{ color: "#ff4d4f", padding: 0, minWidth: 0 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      key={label}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 180px) 1fr",
        gap: "8px 16px",
        alignItems: "center",
        padding: "4px 0",
      }}
    >
      <div style={{ color: "#848696", fontSize: 13 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, wordBreak: "break-word" }}>
        <span>{displayValue}</span>
        <EditOutlined
          onClick={() => setEditing(true)}
          style={{ color: "#6b43b5", cursor: "pointer", fontSize: 12 }}
        />
      </div>
    </div>
  );
}

export default function PolicyInformationCollapse({
  selectedPolicy,
  patient,
  policyCollapseActiveKey,
  setPolicyCollapseActiveKey,
  carrierOptions,
  isLoadingCarriers,
  setPolicyOverrides,
  onPoliciesChanged,
  // The office's contact block, from GET /clinics/office/<id>/insurance-identity/.
  // Fetched once by InsuranceMasterModal and threaded through both tabs; null
  // while loading or when the office can't be resolved, in which case the Office
  // sub-section is simply omitted.
  officeIdentity,
}) {
  if (!selectedPolicy) return null;

  const pt = patient || {};
  const patientName = [pt.first_name, pt.last_name].filter(Boolean).join(" ") || "—";
  const memberName = [selectedPolicy.first_name, selectedPolicy.last_name].filter(Boolean).join(" ") || "—";
  const specialistLabel = selectedPolicy.specialist_fee_covered === true ? "Yes"
    : selectedPolicy.specialist_fee_covered === false ? "No" : "—";

  // Optimistic local merge helper — folds the saved field back into the
  // parent's override map (if provided) and triggers a refetch.
  const applyOverride = (patch) => {
    if (typeof setPolicyOverrides === "function") {
      setPolicyOverrides(function (prev) {
        return { ...prev, [selectedPolicy.id]: { ...prev[selectedPolicy.id], ...patch } };
      });
    }
    if (onPoliciesChanged) onPoliciesChanged();
  };

  return (
    <Collapse
      bordered={false}
      expandIconPosition="start"
      activeKey={policyCollapseActiveKey}
      onChange={function (keys) { setPolicyCollapseActiveKey(keys); }}
      style={{
        marginBottom: 8,
        background: "#f0eaf8",
        border: "1px solid #d8cce8",
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(107, 67, 181, 0.08)",
        overflow: "hidden",
      }}
      styles={{
        header: {
          padding: "8px 14px",
          background: "#e8dff5",
          fontWeight: 600,
          alignItems: "center",
        },
        body: {
          background: "#faf9fc",
          borderTop: "1px solid #dcd0f0",
        },
      }}
      items={[
        {
          key: "policy-patient-plan",
          label: (
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0 16px" }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: "#6b43b5", letterSpacing: 0.2 }}>
                Policy Information
              </span>
              {[
                ["Patient", patientName],
                ["Member", memberName],
                ["Fee Guide", selectedPolicy.fee_guide || "—"],
                ["Specialist Fee", specialistLabel],
                ["Benefit Year", benefitYearSummary(selectedPolicy)],
                ...(selectedPolicy.coverage_scope && selectedPolicy.coverage_scope !== "unknown"
                  ? [["Coverage", planDetailText(PLAN_DETAIL_ROWS.find(function (r) { return r.field === "coverage_scope"; }),
                    selectedPolicy.coverage_scope)]]
                  : []),
                ...((selectedPolicy.benefit_period && selectedPolicy.benefit_period.end) || selectedPolicy.benefit_year_end
                  ? [["Annual max resets", formatAnnualMaxReset(
                    (selectedPolicy.benefit_period && selectedPolicy.benefit_period.end) || selectedPolicy.benefit_year_end)]]
                  : []),
              ].map(function (pair) {
                return (
                  <span key={pair[0]} style={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>
                    <span style={{ color: "#848696" }}>{pair[0]}:</span>{" "}
                    <span style={{ color: "#333", fontWeight: 500 }}>{pair[1]}</span>
                  </span>
                );
              })}
            </div>
          ),
          children: (
            <div
              style={{
                background: "#f8f6fc",
                borderRadius: 8,
                border: "1px solid #e8e4f0",
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px 28px",
                  alignItems: "start",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  {policySubsectionTitle("Patient")}
                  {policySummaryRow("Name", patientName)}
                  {policySummaryRow("DOB", formatPatientDobSummary(pt))}
                  {policySummaryRow("Address", formatPatientAddressSummary(pt))}
                  {policySummaryRow("Email", pt.email)}
                  {policySummaryRow("Phone", pt.phone)}
                  <EditablePolicyField
                    label="Patient relation"
                    value={selectedPolicy.relation_to_member}
                    displayValue={selectedPolicy.relation_to_member_display}
                    fieldName="relation_to_member"
                    policyId={selectedPolicy.id}
                    type="select"
                    options={relationOptions}
                    onSaved={function (updated) {
                      applyOverride({
                        relation_to_member: updated.relation_to_member,
                        relation_to_member_display: updated.relation_to_member_display,
                      });
                    }}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  {policySubsectionTitle("Member")}
                  <EditablePolicyField
                    label="Member first name"
                    value={selectedPolicy.first_name}
                    fieldName="first_name"
                    policyId={selectedPolicy.id}
                    type="text"
                    onSaved={function (updated) { applyOverride({ first_name: updated.first_name }); }}
                  />
                  <EditablePolicyField
                    label="Member last name"
                    value={selectedPolicy.last_name}
                    fieldName="last_name"
                    policyId={selectedPolicy.id}
                    type="text"
                    onSaved={function (updated) { applyOverride({ last_name: updated.last_name }); }}
                  />
                  <EditablePolicyField
                    label="Member DOB"
                    value={selectedPolicy.date_of_birth}
                    fieldName="date_of_birth"
                    policyId={selectedPolicy.id}
                    type="date"
                    onSaved={function (updated) { applyOverride({ date_of_birth: updated.date_of_birth }); }}
                  />
                  <MemberDobSuggestion
                    patientId={patient && patient.id}
                    policyId={selectedPolicy.id}
                    first={selectedPolicy.first_name}
                    last={selectedPolicy.last_name}
                    active={!selectedPolicy.date_of_birth}
                    onUse={async function (dob) {
                      const res = await rootController.updatePatientInsurancePolicy(selectedPolicy.id, { date_of_birth: dob });
                      if (res && res.status === 200) {
                        message.success("Member DOB set from patient record");
                        applyOverride({ date_of_birth: dob });
                      } else {
                        message.error((res && res.json && res.json.error) || "Failed to set DOB");
                      }
                    }}
                  />
                </div>
              </div>
              {officeIdentity ? (
                <div style={{ minWidth: 0 }}>
                  {/* Read-only on purpose: this is office-level data edited on
                      Office -> Portal Credentials, not per-policy, so it must not
                      use EditablePolicyField (which PATCHes the policy). Address
                      and phone use the office's insurance-profile override when
                      set, else the Office record; email is always the Office
                      record, since the profile has no email field. */}
                  {policySubsectionTitle("Office")}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: "0 24px",
                      alignItems: "start",
                    }}
                  >
                    {policySummaryRow("Address", officeIdentity.office_address)}
                    {policySummaryRow("Phone", officeIdentity.office_contact_number)}
                    {policySummaryRow("Email", officeIdentity.office_email)}
                  </div>
                </div>
              ) : null}
              <div style={{ minWidth: 0 }}>
                {policySubsectionTitle("Plan")}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "0 24px",
                    alignItems: "start",
                  }}
                >
                  <EditablePolicyField
                    label="Plan name"
                    value={selectedPolicy.plan_name}
                    fieldName="plan_name"
                    policyId={selectedPolicy.id}
                    type="text"
                    onSaved={function (updated) { applyOverride({ plan_name: updated.plan_name }); }}
                  />
                  <EditablePolicyField
                    label="Carrier"
                    value={selectedPolicy.insurance_name}
                    displayValue={selectedPolicy.insurance_name_display}
                    fieldName="insurance_name"
                    policyId={selectedPolicy.id}
                    type="select"
                    options={carrierOptions}
                    loading={isLoadingCarriers}
                    selectWidth={220}
                    onSaved={function (updated) {
                      applyOverride({
                        insurance_name: updated.insurance_name,
                        insurance_name_display: updated.insurance_name_display,
                      });
                    }}
                  />
                  <EditablePolicyField
                    label="Ordinal"
                    value={selectedPolicy.insurance_type}
                    displayValue={selectedPolicy.insurance_type_display}
                    fieldName="insurance_type"
                    policyId={selectedPolicy.id}
                    type="select"
                    options={ordinalOptions}
                    onSaved={function (updated) {
                      applyOverride({
                        insurance_type: updated.insurance_type,
                        insurance_type_display: updated.insurance_type_display,
                      });
                    }}
                  />
                  <EditablePolicyField
                    label="Group/Contract Number"
                    value={selectedPolicy.contract_number}
                    fieldName="contract_number"
                    policyId={selectedPolicy.id}
                    type="text"
                    onSaved={function (updated) { applyOverride({ contract_number: updated.contract_number }); }}
                  />
                  <EditablePolicyField
                    label="Member/Subscriber ID"
                    value={selectedPolicy.member_id}
                    fieldName="member_id"
                    policyId={selectedPolicy.id}
                    type="text"
                    onSaved={function (updated) { applyOverride({ member_id: updated.member_id }); }}
                  />
                  <EditablePolicyField
                    label="Benefit year start"
                    value={selectedPolicy.benefit_year_start}
                    fieldName="benefit_year_start"
                    policyId={selectedPolicy.id}
                    type="date"
                    onSaved={function (updated) { applyOverride({ benefit_year_start: updated.benefit_year_start, benefit_year: updated.benefit_year }); }}
                  />
                  <EditablePolicyField
                    label="Benefit year end"
                    value={selectedPolicy.benefit_year_end}
                    fieldName="benefit_year_end"
                    policyId={selectedPolicy.id}
                    type="date"
                    onSaved={function (updated) { applyOverride({ benefit_year_end: updated.benefit_year_end }); }}
                  />
                  <EditablePolicyField
                    label="Fee guide"
                    value={selectedPolicy.fee_guide}
                    fieldName="fee_guide"
                    policyId={selectedPolicy.id}
                    type="text"
                    onSaved={function (updated) { applyOverride({ fee_guide: updated.fee_guide }); }}
                  />
                  <EditablePolicyField
                    label="Specialist fee covered"
                    value={selectedPolicy.specialist_fee_covered}
                    fieldName="specialist_fee_covered"
                    policyId={selectedPolicy.id}
                    type="boolean"
                    onSaved={function (updated) { applyOverride({ specialist_fee_covered: updated.specialist_fee_covered }); }}
                  />
                  <EditablePolicyField
                    label="Deductible"
                    value={selectedPolicy.deductible}
                    fieldName="deductible"
                    policyId={selectedPolicy.id}
                    // Free text: carriers state a deductible as a sentence far
                    // more often than as one figure ("$50 per person, $150 per
                    // family", "waived for preventive").
                    type="text"
                    placeholder="e.g. $50 per person, $150 per family"
                    onSaved={function (updated) { applyOverride({ deductible: updated.deductible }); }}
                  />
                </div>
              </div>
              {/* What a full breakdown states about the plan beyond its
                  percentages — each used to live in a note, or nowhere. A blank
                  invites the question; "Unknown" records it was asked. */}
              {PLAN_DETAIL_GROUPS.map(function (group) {
                return (
                  <div key={group.title} style={{ minWidth: 0 }}>
                    {policySubsectionTitle("Plan details · " + group.title)}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: "0 24px",
                        alignItems: "start",
                      }}
                    >
                      {group.rows.map(function (row) {
                        return (
                          <EditablePolicyField
                            key={row.field}
                            label={row.label}
                            value={selectedPolicy[row.field]}
                            displayValue={planDetailText(row, selectedPolicy[row.field])}
                            fieldName={row.field}
                            policyId={selectedPolicy.id}
                            type={row.type}
                            options={row.options}
                            placeholder={row.placeholder}
                            onSaved={function (updated) {
                              applyOverride({ [row.field]: updated[row.field] });
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ),
        },
      ]}
    />
  );
}
